import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CostCentersService } from '../../../../services/cost-centers.service';
import { StaffService } from '../../../../services/staff.service';
import { CostCenter } from '../../../../models/CostCenter';
import { CostCenterBudgetHistoryEntry } from '../../../../models/CostCenterBudgetHistory';
import { CostCenterExpenseBreakdown } from '../../../../models/CostCenterExpenseBreakdown';
import { formatBrazilDateTime } from '../../../../utils/datetime.utils';

interface HistoryRow extends CostCenterBudgetHistoryEntry {
  effectiveAtLabel: string;
  changeTypeLabel: string;
  changedByLabel: string;
  monthlyLabel: string;
  annualLabel: string;
}

@Component({
  selector: 'app-cost-center-view',
  templateUrl: './cost-center-view.component.html',
  styleUrls: ['./cost-center-view.component.css']
})
export class CostCenterViewComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private staffNameCache = new Map<number, string>();

  costCenter: CostCenter | null = null;
  costCenterCode = '';
  history: HistoryRow[] = [];
  isLoading = false;
  isLoadingHistory = false;
  error: string | null = null;
  historyError: string | null = null;

  expenseBreakdown: CostCenterExpenseBreakdown | null = null;
  includeChildrenInBreakdown = true;
  isLoadingBreakdown = false;
  breakdownError: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private costCentersService: CostCentersService,
    private staffService: StaffService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const code = (params.get('code') || '').trim();
      if (!code) {
        this.error = 'Código do centro de custo não informado.';
        return;
      }
      this.costCenterCode = code;
      this.loadByCode();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadByCode(): void {
    this.isLoading = true;
    this.error = null;
    this.costCenter = null;
    this.history = [];
    this.historyError = null;
    this.expenseBreakdown = null;
    this.breakdownError = null;

    this.costCentersService
      .getByCode(this.costCenterCode, { suppressErrorToast: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cc) => {
          this.costCenter = cc;
          this.isLoading = false;
          if (cc.budgetHistories?.length) {
            this.applyHistoryEntries(cc.budgetHistories);
          } else if (cc.id > 0) {
            this.loadBudgetHistory(cc.id);
          }
          if (cc.id > 0) {
            this.loadExpenseBreakdown(cc.id);
          }
        },
        error: () => {
          this.isLoading = false;
          this.error = `Centro de custo com código "${this.costCenterCode}" não encontrado.`;
        }
      });
  }

  loadExpenseBreakdown(costCenterId: number): void {
    this.isLoadingBreakdown = true;
    this.breakdownError = null;
    this.costCentersService
      .getExpenseBreakdown(costCenterId, this.includeChildrenInBreakdown, {
        suppressErrorToast: true
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.expenseBreakdown = data;
          this.isLoadingBreakdown = false;
        },
        error: () => {
          this.isLoadingBreakdown = false;
          this.expenseBreakdown = null;
          this.breakdownError = 'Não foi possível carregar o acompanhamento de gastos.';
        }
      });
  }

  onIncludeChildrenChange(): void {
    if (this.costCenter?.id) {
      this.loadExpenseBreakdown(this.costCenter.id);
    }
  }

  loadBudgetHistory(costCenterId: number): void {
    this.isLoadingHistory = true;
    this.historyError = null;
    this.costCentersService
      .getBudgetHistory(costCenterId, { suppressErrorToast: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (entries) => {
          this.applyHistoryEntries(entries || []);
        },
        error: () => {
          this.isLoadingHistory = false;
          this.historyError = 'Não foi possível carregar o histórico de orçamento.';
          this.history = [];
        }
      });
  }

  goBack(): void {
    this.router.navigate(['/financial/cost-centers']);
  }

  editCostCenter(): void {
    if (this.costCenter?.id) {
      this.router.navigate(['/financial/cost-centers', this.costCenter.id, 'edit']);
    }
  }

  viewProposal(proposalId: number | undefined): void {
    if (proposalId != null && proposalId > 0) {
      this.router.navigate(['/financial/budget-proposals', proposalId, 'view']);
    }
  }

  viewAccountsPayable(accountsPayableId: number | undefined): void {
    if (accountsPayableId != null && accountsPayableId > 0) {
      this.router.navigate(['/financial/accounts-payable', accountsPayableId, 'view']);
    }
  }

  expenseStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s.includes('pago') || s.includes('paid')) return 'bg-success';
    if (s.includes('venc') || s.includes('overdue')) return 'bg-danger';
    if (s.includes('pend') || s.includes('pending')) return 'bg-warning text-dark';
    return 'bg-secondary';
  }

  formatDate(value?: string): string {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('pt-BR');
  }

  displayResponsibleName(): string {
    const name = (this.costCenter?.managerStaffName || this.costCenter?.responsible || '').trim();
    return name || '—';
  }

  displayCurrency(): string {
    return (this.costCenter?.currencyCode || 'BRL').toUpperCase();
  }

  private applyHistoryEntries(entries: CostCenterBudgetHistoryEntry[]): void {
    this.history = (entries || []).map((e) => this.toHistoryRow(e));
    this.isLoadingHistory = false;
    this.historyError = null;
    this.resolveStaffNamesForHistory(entries || []);
  }

  formatMoney(value: number | undefined | null): string {
    if (value == null || !Number.isFinite(Number(value))) {
      return '—';
    }
    const currency = this.costCenter?.currencyCode || 'BRL';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(Number(value));
  }

  formatDateTime(value?: string): string {
    return formatBrazilDateTime(value);
  }

  private toHistoryRow(e: CostCenterBudgetHistoryEntry): HistoryRow {
    return {
      ...e,
      effectiveAtLabel: this.formatDateTime(e.effectiveAt),
      changeTypeLabel: this.changeTypeLabel(e.changeType),
      changedByLabel: this.staffLabel(e.changedByStaffId),
      monthlyLabel: this.formatMoney(e.newMonthlyBudget),
      annualLabel: this.formatMoney(e.newAnnualBudget)
    };
  }

  private changeTypeLabel(type: string): string {
    const t = (type || '').trim();
    const map: Record<string, string> = {
      Submitted: 'Proposta enviada',
      ApprovedProposal: 'Proposta aprovada',
      RejectedProposal: 'Proposta recusada',
      ManualAdjustment: 'Ajuste manual',
      Cancelled: 'Cancelado'
    };
    return map[t] || t || '—';
  }

  private staffLabel(staffId?: number): string {
    if (staffId == null || staffId <= 0) {
      return '—';
    }
    return this.staffNameCache.get(staffId) || `Colaborador #${staffId}`;
  }

  private resolveStaffNamesForHistory(entries: CostCenterBudgetHistoryEntry[]): void {
    const ids = new Set<number>();
    for (const e of entries) {
      if (e.changedByStaffId != null && e.changedByStaffId > 0) {
        ids.add(e.changedByStaffId);
      }
    }
    ids.forEach((id) => {
      if (this.staffNameCache.has(id)) {
        this.refreshHistoryStaffLabels();
        return;
      }
      this.staffService
        .getStaffById(id, { suppressErrorToast: true })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (s) => {
            const name = (s.FullName || '').trim();
            if (name) {
              this.staffNameCache.set(id, name);
            }
            this.refreshHistoryStaffLabels();
          }
        });
    });
  }

  private refreshHistoryStaffLabels(): void {
    this.history = this.history.map((row) => ({
      ...row,
      changedByLabel: this.staffLabel(row.changedByStaffId)
    }));
  }
}
